import React, { useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCompress, faExpand } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";

const InfoRow = ({ label, value }) => (
  <div className="space-y-1">
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-sm font-semibold text-slate-900 break-words">
      {value || "N/A"}
    </p>
  </div>
);

const DEFAULT_ALLOWED_EXTRACTED_COVERAGES = [
  "OUTPATIENT-OPD-OR",
  "INPATIENT-ADMISSION",
];
const DEFAULT_EXCLUDED_BATCH_RULES = [
  { coverage_code: "INPATIENT-ADMISSION", claim_status: "EXTRACTED" },
  { coverage_code: "OUTPATIENT-OPD-OR", claim_status: "EXTRACTED" },
];

const normalizeCoverageList = (values) => {
  if (!Array.isArray(values)) return [];
  const normalized = values
    .map((value) => String(value || "").trim().toUpperCase())
    .filter((value) => value !== "");
  return Array.from(new Set(normalized));
};

const normalizeExcludedRules = (values) => {
  if (!Array.isArray(values)) return [];
  const normalized = values
    .map((rule) => {
      if (!rule || typeof rule !== "object") return null;
      const coverage = String(
        rule.coverage_code || rule.coverage || ""
      ).trim().toUpperCase();
      const status = String(
        rule.claim_status || rule.status || ""
      ).trim().toUpperCase();
      if (!coverage || !status) return null;
      return { coverage_code: coverage, claim_status: status };
    })
    .filter(Boolean);
  const unique = new Map();
  normalized.forEach((rule) => {
    unique.set(`${rule.coverage_code}:${rule.claim_status}`, rule);
  });
  return Array.from(unique.values());
};

const formatDateForCopy = (value) => {
  if (!value) return "";
  const parts = String(value).split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    const [year, month, day] = parts;
    return `${month.padStart(2, "0")}-${day.padStart(2, "0")}-${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

const copyToClipboard = async (text) => {
  if (!text) return false;
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const success = document.execCommand("copy");
  document.body.removeChild(textarea);
  return success;
};

const ScanLoaConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  data,
  loading = false,
}) => {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const phpFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return value;
    return phpFormatter.format(numeric);
  };

  const handleCopyDateReceived = async () => {
    const rawValue = data?.dateReceived || "";
    if (!rawValue) {
      toast.error("Please select Date Received first.");
      return;
    }
    const formatted = formatDateForCopy(rawValue);
    if (!formatted) {
      toast.error("Invalid Date Received value.");
      return;
    }
    try {
      const copied = await copyToClipboard(formatted);
      if (copied) {
        toast.success(`Date Received copied: ${formatted}`);
      } else {
        toast.error("Unable to copy Date Received. Please try again.");
      }
    } catch (error) {
      toast.error("Unable to copy Date Received. Please try again.");
    }
  };

  const loaRows = data?.loaRows || [];
  const allowedExtractedCoverages = useMemo(() => {
    const raw = data?.allowedExtractedCoverages;
    if (!Array.isArray(raw)) {
      return DEFAULT_ALLOWED_EXTRACTED_COVERAGES;
    }
    return normalizeCoverageList(raw);
  }, [data]);
  const excludedBatchRules = useMemo(() => {
    const raw = data?.excludedBatchRules;
    if (!Array.isArray(raw)) {
      return DEFAULT_EXCLUDED_BATCH_RULES;
    }
    const normalized = normalizeExcludedRules(raw);
    return normalized.length ? normalized : DEFAULT_EXCLUDED_BATCH_RULES;
  }, [data]);
  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return loaRows;
    return loaRows.filter((row) => {
      const loaNo = row.loaNo?.toString().toLowerCase() ?? "";
      const coverage = row.coverageCode?.toString().toLowerCase() ?? "";
      const date = row.dateReceived?.toString().toLowerCase() ?? "";
      return (
        loaNo.includes(term) || coverage.includes(term) || date.includes(term)
      );
    });
  }, [loaRows, searchTerm]);

  const isAllowedExtractedCoverage = (row) => {
    const status = (row.claimStatus || "").trim().toLowerCase();
    if (status !== "extracted") return false;
    const coverage = (row.coverageCode || "").trim().toUpperCase();
    return allowedExtractedCoverages.includes(coverage);
  };

  /*
  // OLD SPEC (receive-only excluded):
  const isReceiveOnlyRow = (row) => {
    const status = (row.claimStatus || "").trim().toUpperCase();
    if (!status) return false;
    const coverage = (row.coverageCode || "").trim().toUpperCase();
    return excludedBatchRules.some(
      (rule) =>
        rule.coverage_code === coverage && rule.claim_status === status
    );
  };

  const isBlockedRow = (row) => {
    const status = (row.claimStatus || "").trim();
    if (!status) return false;
    if (isReceiveOnlyRow(row)) return false;
    return !isAllowedExtractedCoverage(row);
  };
  */

  // NEW SPEC: no receive-only exclusion; extracted + allowed coverages can be batched.
  const isBlockedRow = (row) => {
    const status = (row.claimStatus || "").trim();
    if (!status) return false;
    return !isAllowedExtractedCoverage(row);
  };

  const blockedRows = useMemo(
    () => loaRows.filter(isBlockedRow),
    [loaRows, allowedExtractedCoverages, excludedBatchRules]
  );
  const enteredClaimCount = Number(data?.actualClaimCount);
  const hasValidClaimCount =
    Number.isInteger(enteredClaimCount) && enteredClaimCount >= 0;
  const claimCountMatches =
    hasValidClaimCount && enteredClaimCount === loaRows.length;
  const confirmDisabled = loading || blockedRows.length > 0 || !claimCountMatches;
  /*
  // OLD SPEC (receive-only + RTP excluded):
  const receiveOnlyRows = useMemo(
    () => loaRows.filter(isReceiveOnlyRow),
    [loaRows, excludedBatchRules]
  );
  const rtpRows = useMemo(() => loaRows.filter((row) => row.rtpTag), [loaRows]);
  */
  const allowedCoverageLabel = useMemo(() => {
    if (allowedExtractedCoverages.length === 0) return "None";
    return allowedExtractedCoverages.join(", ");
  }, [allowedExtractedCoverages]);

  const totalLoaAmount = useMemo(
    () =>
      loaRows.reduce(
        (sum, row) => sum + (Number(row.loaAmount) || 0),
        0
      ),
    [loaRows]
  );
  const totalClaimAmount = useMemo(
    () =>
      loaRows.reduce(
        (sum, row) => sum + (Number(row.claimAmount) || 0),
        0
      ),
    [loaRows]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          className={`w-full rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl overflow-y-auto ${
            isExpanded ? "max-w-6xl max-h-[95vh]" : "max-w-5xl max-h-[92vh]"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Confirmation Review
              </p>
              <h2 className="text-2xl font-semibold text-slate-900">
                Scan LOA Document QR - Review
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsExpanded((prev) => !prev)}
                className="text-slate-400 hover:text-slate-600 transition"
                aria-label={isExpanded ? "Restore size" : "Maximize"}
              >
                <FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} />
              </button>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600 transition"
                aria-label="Close"
              >
                x
              </button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Back
            </button>
            <button
              onClick={onConfirm}
              disabled={confirmDisabled}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {loading ? "Submitting..." : "Confirm & Create Batch"}
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <InfoRow label="Provider" value={data?.providerName} />
            <InfoRow label="Terms" value={data?.paymentTerms} />
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Date Received
              </p>
              <p className="text-sm font-semibold text-slate-900 break-words">
                {data?.dateReceived || "N/A"}
              </p>
              <button
                type="button"
                onClick={handleCopyDateReceived}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
              >
                Copy Date
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <InfoRow label="Due Date" value={data?.dueDate} />
            <InfoRow label="Received By" value={data?.receivedBy} />
            <InfoRow label="SOA/Ref#" value={data?.soaRef} />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <InfoRow label="SOA Amount" value={formatAmount(data?.soaAmount)} />
            <InfoRow
              label="Actual Claim Count"
              value={hasValidClaimCount ? enteredClaimCount : "N/A"}
            />
            <InfoRow label="Attached LOA Count" value={loaRows.length} />
          </div>

          {!claimCountMatches && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              Claim Count does not match the attached LOA count. Please review the batch before continuing.
            </div>
          )}

          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">
              Batch Remarks
            </p>
            <p className="mt-2 text-sm text-slate-700 min-h-[40px]">
              {data?.remarks || "None"}
            </p>
          </div>

          <div className="mt-6 rounded-xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <p className="text-sm font-semibold text-slate-700">
                LOA List ({filteredRows.length})
              </p>
              <p className="text-xs text-slate-500">
                Page {page} of {totalPages}
              </p>
            </div>
            {blockedRows.length > 0 && (
              <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                {blockedRows.length} LOA cannot be batched because Claim Status is not empty.
                (Exception: Extracted + {allowedCoverageLabel} allowed.)
                Remove them to proceed.
              </div>
            )}
            {/*
              OLD SPEC (receive-only + RTP excluded):
              {receiveOnlyRows.length > 0 && (
                <div className="border-b border-sky-200 bg-sky-50 px-4 py-3 text-xs text-sky-800">
                  {receiveOnlyRows.length} LOA marked as Receive-only. They will be recorded
                  in Date Received but excluded from batch creation.
                </div>
              )}
              {rtpRows.length > 0 && (
                <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                  {rtpRows.length} LOA marked as For RTP. They will be received but excluded
                  from batch creation.
                </div>
              )}
            */}
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex-1 min-w-[240px]">
                <p className="text-xs font-semibold uppercase text-slate-500">
                  Total LOA Amount
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {formatAmount(totalLoaAmount)}
                </p>
              </div>
              <div className="flex-1 min-w-[240px]">
                <p className="text-xs font-semibold uppercase text-slate-500">
                  Total Claim Amount
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {formatAmount(totalClaimAmount)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">Search</div>
              <input
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setPage(1);
                }}
                placeholder="Search LOA#, Coverage, Date Received"
                className="w-full max-w-xs rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-600 focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">LOA#</th>
                    <th className="px-4 py-3">Coverage</th>
                    <th className="px-4 py-3">LOA Amount</th>
                    <th className="px-4 py-3">Claim Amount</th>
                    <th className="px-4 py-3">Claim Status</th>
                    <th className="px-4 py-3">Staled</th>
                    <th className="px-4 py-3">Date Received</th>
                    <th className="px-4 py-3">Received By</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-6 text-center text-sm text-slate-500"
                      >
                        No LOA records available.
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((row) => (
                      // {/* OLD SPEC (receive-only styling):
                      // <tr
                      //   key={row.loaNo}
                      //   className={`border-t ${
                      //     isBlockedRow(row)
                      //       ? "border-red-200 bg-red-50"
                      //       : isReceiveOnlyRow(row)
                      //       ? "border-sky-200 bg-sky-50"
                      //       : row.claimStatus
                      //       ? "border-amber-200 bg-amber-50"
                      //       : "border-slate-100"
                      //   }`}
                      // >
                      // */}
                      <tr
                        key={row.loaNo}
                        className={`border-t ${
                          isBlockedRow(row)
                            ? "border-red-200 bg-red-50"
                            : row.claimStatus
                            ? "border-amber-200 bg-amber-50"
                            : "border-slate-100"
                        }`}
                      >
                        <td className="px-4 py-3 font-semibold text-slate-800">
                          {row.loaNo}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {row.coverageCode}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatAmount(row.loaAmount)}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatAmount(row.claimAmount)}
                        </td>
                        {/* OLD SPEC (receive-only status badge):
                        <td className="px-4 py-3">
                          {row.claimStatus ? (
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                isBlockedRow(row)
                                  ? "bg-red-100 text-red-700"
                                  : isReceiveOnlyRow(row)
                                  ? "bg-sky-100 text-sky-700"
                                  : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              {isAllowedExtractedCoverage(row)
                                ? `${row.claimStatus} (Allowed)`
                                : isReceiveOnlyRow(row)
                                ? `${row.claimStatus} (Receive-only)`
                                : row.claimStatus}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                              Available
                            </span>
                          )}
                        </td>
                        */}
                        <td className="px-4 py-3">
                          {row.claimStatus ? (
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                isBlockedRow(row)
                                  ? "bg-red-100 text-red-700"
                                  : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              {isAllowedExtractedCoverage(row)
                                ? `${row.claimStatus} (Allowed)`
                                : row.claimStatus}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                              Available
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1 text-xs">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 font-semibold ${
                                row.stale
                                  ? "bg-rose-100 text-rose-700"
                                  : "bg-emerald-100 text-emerald-700"
                              }`}
                            >
                              {row.stale ? "Staled" : "No"}
                            </span>
                            {row.rtpTag && (
                              <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-700">
                                For RTP
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {row.dateReceived}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {row.receivedBy}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
                className="rounded-md border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                className="rounded-md border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Back
            </button>
            <button
              onClick={onConfirm}
              disabled={confirmDisabled}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {loading ? "Submitting..." : "Confirm & Create Batch"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ScanLoaConfirmationModal;
